import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Package, BarChart3, Box, Activity, ShoppingBag } from "lucide-react";
import RolePreviewLayout from "@/components/welcome/RolePreviewLayout";
import { MultiLayerValue } from "@/components/welcome/MultiLayerValue";
import { analytics } from "@/lib/analytics";

const accent = {
  text: "text-cyan-400", border: "border-cyan-400/30",
  bgSoft: "bg-cyan-400/5", bg: "bg-cyan-400",
};

export default function WelcomeDownstream() {
  const [params] = useSearchParams();
  const name = params.get("name") || "there";
  useEffect(() => { analytics.rolePreviewViewed("downstream", "a"); }, []);

  return (
    <>
      <RolePreviewLayout
        role="downstream"
        accent={accent}
        eyebrow="Application received · Distributor / Retail Buyer"
        headline={<>Welcome, <span className="text-cyan-400">{name}</span>. <br className="hidden sm:block" />Plan better. Stock right. Sell more.</>}
        subline="As a verified downstream buyer, you tap a network of pre-vetted importers with reliable supply, real-time milestone tracking, and predictable inventory rhythms. Fewer stockouts. Less waste. Stronger margins."
        primaryStat={{ value: "15–25%", label: "Reduction in stockouts" }}
        benefits={[
          { icon: BarChart3, title: "Better inventory planning", desc: "Forecast accuracy improves 5-10% with real-time shipment & milestone visibility." },
          { icon: Box, title: "Less excess inventory", desc: "10-20% reduction in held stock — lower carrying costs, lower wastage, healthier cash." },
          { icon: ShoppingBag, title: "Reliable, predictable supply", desc: "Pre-vetted importers + milestone-validated shipments mean fewer surprises and on-shelf availability." },
        ]}
        dashboard={
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                ["Active SKUs", "47", "text-white"],
                ["On-shelf rate", "94%", "text-cyan-400"],
                ["Forecast accuracy", "82%", "text-[#22C55E]"],
                ["Verified importers", "6", "text-purple-400"],
              ].map(([l, v, c]) => (
                <div key={l} className="bg-[#050A07] border border-[#1A2E22] p-3">
                  <p className="text-[#A1B2A8] text-[10px] uppercase tracking-wider">{l}</p>
                  <p className={`font-['Outfit'] text-xl font-bold ${c}`}>{v}</p>
                </div>
              ))}
            </div>
            <div className="bg-[#050A07] border border-[#1A2E22] p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white text-sm font-semibold">Q2 plan · 12 MT Frozen Shrimp · 3 SKUs</span>
                <span className="text-cyan-400 text-[10px] border border-cyan-400/30 px-2 py-0.5">On track</span>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3 text-xs">
                <div><p className="text-[#A1B2A8]/60">Stockout risk</p><p className="text-[#22C55E] font-semibold">2.1%</p></div>
                <div><p className="text-[#A1B2A8]/60">Excess risk</p><p className="text-yellow-400 font-semibold">7.4%</p></div>
                <div><p className="text-[#A1B2A8]/60">Margin lift</p><p className="text-[#22C55E] font-semibold">+3.2%</p></div>
              </div>
            </div>
          </div>
        }
        nextSteps={[
          { title: "KYC verification (1-2 days)", desc: "Business license + GST/Tax registration + bank reference. Auto-pulled where possible." },
          { title: "Inventory connect", desc: "Sync your weekly forecast or POS feed. We map it to inbound milestones." },
          { title: "First reliable inflow", desc: "Pre-vetted importer network ships to your milestone-aligned plan — fewer stockouts on shelf, fewer rush orders." },
        ]}
      />
      {/* Slide 9 multi-layer reveal */}
      <MultiLayerValue persona="downstream" />
    </>
  );
}
